// REACT
import React, { useEffect, useState, useRef, SyntheticEvent, useContext, useMemo } from 'react';
// YARN
import { toast } from 'react-toastify';
import { SwatchesPicker } from 'react-color';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
// SERVICES
import { addCulture, getCoProductsPerCooperative, updateCulture } from 'services/API/Cooperative';
import {
  checkDecimalNegativeDatagrid,
  checkDecimalRegexTwoDigitsDatagridValue,
  checkNoDecimalRegexTwoDigitsDatagridValue,
  checkTypeStringRegexDatagrid,
  emptyFieldToZero,
} from 'utils/tools_functions';
import {
  inputRequired,
  inputIntegerValidator,
  inputPositiveFloatValidator,
  inputNumberRequired,
  inputAbsolutePositiveFloatValidator,
  inputPositiveNotNullFloatValidator,
  inputExtraCostValidator,
  inputVerifyName,
  inputIrrigationYieldGain,
  inputIFTValidator,
  inputYieldValidator,
  inputDurationValidator,
  inputVerifyCode,
  inputUnique,
  inputVerifySeason,
  inputVerifyCultureAndPresenceDuration,
} from 'components/generics/Validators/validator';
import {
  validateFormData,
  validateFormDataMetaCulture,
} from 'components/generics/Validators/form.validate';

// INTERFACES
import { ICulture, getCultureSeasonLabels } from 'components/generics/Interface/ICulture';
import { IFormErrorObjects, IFormErrors } from 'components/generics/Interface/Commons/IErrorForm';
import { IEconomicAttributeCollection } from 'components/generics/Interface/IEconomicAttributeCollection';
import { ICoproductType } from 'components/generics/Interface/ICoproduct';
// THEMES
import { customDialogAddCultureTheme } from 'assets/styles/themes/Dialog/generic_dialog_add_culture';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { IMetaculture } from 'components/generics/Interface/IMetaculture';
import useAutoComplete from 'utils/hooks/useAutoComplete';
import { ILoadingContext } from 'components/generics/Interface/Context/ILoadingContext';
import { LoadingContext } from 'utils/context';
import { useTranslation } from 'react-i18next';
import { CULTURE_TYPE, getCultureTypeLabels } from 'utils/GlobalVariables';

const DEFAULT_FORM = {
  id: null,
  name: '',
  economicAttributeCollection: {
    price: {
      currentEconomicValue: '',
      futureEconomicValue: '',
    },
    inputCostWithoutNitrogen: {
      currentEconomicValue: '',
      futureEconomicValue: '',
    },
  },
  tfiHerbicide: 0,
  tfiWithoutHerbicide: 0,
  presenceDuration: '',
  prohibitionDuration: '',
  yield: '',
  irrigationYieldGain: '',
  irrigationWaterNeeded: '',
  coProducts: [],
  cultureSeason: '',
  isStrawProducer: false,
  isArable: true,
  capCategory: '',
  extraCost: 0,
  isIrrigableOnly: false,
  color: undefined,
  nitrogenBonus: 0,
  nitrogenLimit: 0,
  aerialBiomass: 0,
  rootBiomass: 0,
  metaCode: '',
  metaCultureName: '',
  isOrganic: false,
  isCustomized: true,
  firstSimulateEnabled: true,
  cultureType: CULTURE_TYPE.main,
};

type FormData = {
  id?: number | null;
  name: string;
  economicAttributeCollection: IEconomicAttributeCollection;
  tfiHerbicide: number;
  tfiWithoutHerbicide: number;
  presenceDuration: string;
  prohibitionDuration: string;
  yield: string;
  irrigationYieldGain: string;
  irrigationWaterNeeded: string;
  coProducts: CoproductWithoutName[];
  cultureSeason: string;
  isStrawProducer: boolean;
  isArable: boolean;
  capCategory: string | null;
  extraCost: number;
  isIrrigableOnly: boolean;
  color: undefined | string;
  nitrogenBonus: number;
  nitrogenLimit: number;
  aerialBiomass: number;
  rootBiomass: number;
  metaCode: string | null;
  metaCultureName: string | null;
  isOrganic: boolean;
  isCustomized: boolean;
  firstSimulateEnabled: boolean;
  cultureType: string;
};

type BlankCoProduct = { id: number | string; displayName: string };

type CoproductWithoutName = {
  id: number | string;
  displayName: string;
  yield?: number | string;
};

type Color = {
  hex: string;
  [key: string]: any;
};

type AddCultureModalParams = {
  modal: boolean;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitSuccess: Function;
  role: string;
  cultures: ICulture[];
  metaculture: IMetaculture[];
  isOrganic: boolean;
  invertedCultures: ICulture[];
};

const AddCultureModal = ({
  modal,
  setModal,
  onSubmitSuccess,
  role = '',
  cultures,
  metaculture,
  isOrganic,
  invertedCultures,
}: AddCultureModalParams) => {
  const { loading, setFullScreen, setCustomText } = useContext(LoadingContext) as ILoadingContext;
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);
  const [coProductsTypes, setCoProductsTypes] = useState<ICoproductType[]>([]);
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);
  const [errorsFormCoProduct, setErrorsFormCoProduct] = useState<IFormErrors>({
    formError: false,
    exception: true,
    yield: {
      message: '',
      validator: [inputNumberRequired, inputAbsolutePositiveFloatValidator],
    },
  });
  const [errorsFormEconomicValue, setErrorsFormEconomicValue] = useState<IFormErrors>({
    formError: false,
    price: {
      message: '',
      validator: [inputNumberRequired, inputPositiveFloatValidator],
    },
    priceN5: {
      message: '',
      validator: [inputPositiveFloatValidator],
    },
    inputCostWithoutNitrogen: {
      message: '',
      validator: [inputNumberRequired, inputPositiveFloatValidator],
    },
    inputCostWithoutNitrogenN5: {
      message: '',
      validator: [inputPositiveFloatValidator],
    },
  });
  const [errorCoProductsMessages, setErrorCoProductsMessages] = useState<string[]>([]);
  const [errorsFormDefault, setErrorsFormDefault] = useState<IFormErrors>({
    formError: false,
    name: {
      message: '',
      validator: [inputRequired, inputVerifyName, inputUnique],
    },
    extraCost: {
      message: '',
      validator: [inputExtraCostValidator],
    },
    tfiHerbicide: {
      message: '',
      validator: [inputPositiveNotNullFloatValidator, inputIFTValidator],
    },
    tfiWithoutHerbicide: {
      message: '',
      validator: [inputPositiveNotNullFloatValidator, inputIFTValidator],
    },
    presenceDuration: {
      message: '',
      params: ['cultureType', 'cultureSeason'],
      validator: [
        inputNumberRequired,
        inputIntegerValidator,
        inputDurationValidator,
        inputVerifyCultureAndPresenceDuration,
      ],
    },
    prohibitionDuration: {
      message: '',
      validator: [inputNumberRequired, inputIntegerValidator, inputDurationValidator],
    },
    yield: {
      message: '',
      validator: [inputNumberRequired, inputPositiveFloatValidator, inputYieldValidator],
    },
    irrigationYieldGain: {
      message: '',
      validator: [inputNumberRequired, inputPositiveFloatValidator, inputIrrigationYieldGain],
    },
    irrigationWaterNeeded: {
      message: '',
      validator: [inputNumberRequired, inputPositiveFloatValidator],
    },
    cultureSeason: {
      message: '',
      params: 'cultureType',
      validator: [inputVerifySeason],
    },
  });

  const [errorsFormMetaCulture, setErrorsFormMetaCulture] = useState<IFormErrors>({
    formError: false,
    metaCode: {
      message: '',
      validator: [inputVerifyCode],
    },
  });
  const [selectedAssociateCulture, setSelectedAssociateCulture] = useState<string | number | null>(
    null,
  );
  const defaultCulture: Partial<ICulture> = {
    id: 0,
    name: t('form.noCorrespondingCulture'),
    isCustomized: false,
  };

  const [listAssociateCulture, setListAssociateCulture] = useState<ICulture[]>([
    defaultCulture as ICulture,
    ...(invertedCultures || []),
  ]);

  useEffect(() => {
    setFormData({
      ...formData,
      isOrganic: isOrganic ?? false,
    });
  }, [isOrganic]);

  const resetMetacultureClone = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      metaCode: '',
      metaCultureName: '',
      rootBiomass: 0,
      aerialBiomass: 0,
      isArable: true,
      capCategory: '',
    }));
    setInputValue('');
  };

  const { renderAutoComplete, inputValue, setInputValue, metaCode, metaCultureName } =
    useAutoComplete<IMetaculture>({
      data: metaculture,
      errorsFormDefault: errorsFormMetaCulture,
      field: 'metaCode',
      initialValue: formData?.name,
      resetValue: resetMetacultureClone,
      selectedAssociateValue: selectedAssociateCulture,
      getOptionLabel: (option) => `${option.metaCode}-${option.metaCultureName}`,
      isMetaculture: true,
    });

  const saisonCultures = useMemo(
    () => [
      { value: '', label: t('common.none') },
      { value: 'winter', label: getCultureSeasonLabels(t).winter },
      { value: 'summer', label: getCultureSeasonLabels(t).summer },
      { value: 'spring', label: getCultureSeasonLabels(t).spring },
    ],
    [t],
  );

  const cultureTypes = useMemo(
    () => [
      { value: CULTURE_TYPE.main, label: getCultureTypeLabels(t).main },
      { value: CULTURE_TYPE.intermediate, label: getCultureTypeLabels(t).intermediate },
    ],
    [t],
  );

  const blankCoProduct = {
    id: 0,
    displayName: t('common.none'),
  };
  const [coProductState, setCoProductState] = useState<BlankCoProduct[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFullScreen(true);
    let cooperativeId = null;
    if (role && role === 'SUPER_ADMIN') cooperativeId = Number(localStorage.getItem('cooperative'));
    getCoProductsPerCooperative(cooperativeId)
      .then((e) => {
        setCoProductsTypes([{ id: 0, displayName: t('common.none') }].concat(e.data.data));
      })
      .catch((err) => {
        toast.error(err.response.data.message);
      });
  }, []);

  const addCoProduct = () => {
    setCoProductState((prevCoProductState) => [...prevCoProductState, blankCoProduct]);
    return <div></div>;
  };

  const lastCoProduct: CoproductWithoutName = formData.coProducts.slice(-1)[0];
  const lastCoProductState: CoproductWithoutName = coProductState.slice(-1)[0];

  const checkDisabledCoProductButton =
    formData.coProducts && Object.keys(formData.coProducts).length
      ? !lastCoProduct.id || !lastCoProduct.yield || !lastCoProductState.id
        ? true
        : false
      : true;

  const checkDisabledCoProduct = (index: number) => {
    if (coProductState && coProductState[index + 1]) {
      return true;
    }
    return false;
  };

  const processedCoProductTypes = (idx: number) => {
    if (idx >= 1) {
      const previousCoProducts = formData.coProducts.slice(0, idx);

      return coProductsTypes.filter(
        (coProductType) =>
          !previousCoProducts.some(
            (previousCoProduct) => previousCoProduct.id === coProductType.id,
          ),
      );
    }

    return coProductsTypes;
  };

  const removeCoProduct = (key: number) => {
    if (formData.coProducts && Object.keys(formData.coProducts).length === 1) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        coProducts: [blankCoProduct],
      }));
    } else {
      if (!Object.keys(formData.coProducts).length) {
        return;
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        coProducts: prevFormData.coProducts.filter((_, idx) => idx !== key),
      }));
    }
    setCoProductState((prevCoProductState) => prevCoProductState.filter((_, idx) => idx !== key));
  };

  const updateCoProduct = (key: number, updatedCoProduct: CoproductWithoutName) => {
    setCoProductState((prevCoProductState: BlankCoProduct[]) =>
      prevCoProductState.map((prevCoProductStateItem: BlankCoProduct, index: number) =>
        index === key
          ? {
              ...prevCoProductStateItem,
              id: updatedCoProduct?.id,
              displayName: updatedCoProduct.displayName,
            }
          : prevCoProductStateItem,
      ),
    );
  };

  const handleClick = () => {
    setDisplayColorPicker(true);
  };
  const handleClose = () => {
    setDisplayColorPicker(false);
  };

  const checkUniqueColor = (newColor: Color) => {
    const existingColorCulture = cultures?.find((culture) => culture.color === newColor?.hex);
    if (existingColorCulture) {
      toast.error(t('errors.colorAlreadyUsed', { name: existingColorCulture?.name }));
      return false;
    }
    return true;
  };

  const copyCurrentValueToFuture = () => {
    for (const key in formData?.economicAttributeCollection) {
      const economicType =
        formData.economicAttributeCollection[
          key as keyof typeof formData.economicAttributeCollection
        ];
      if (
        !economicType.futureEconomicValue &&
        economicType.futureEconomicValue != '0' &&
        economicType.currentEconomicValue
      ) {
        economicType.futureEconomicValue = economicType.currentEconomicValue;
      }
    }
  };

  const onSubmit = (e: SyntheticEvent) => {
    if (e) e.preventDefault();
    copyCurrentValueToFuture();
    errorsFormEconomicValue.formError = false;
    const economicAttributeCollectionCopy: { [key: string]: any } = {};
    for (const key in formData?.economicAttributeCollection) {
      const economicType =
        formData.economicAttributeCollection[
          key as keyof typeof formData.economicAttributeCollection
        ];
      for (const subKey in economicType) {
        if (subKey === 'currentEconomicValue') {
          economicAttributeCollectionCopy[key] =
            formData.economicAttributeCollection[key as keyof IEconomicAttributeCollection][subKey];
        } else if (subKey === 'futureEconomicValue') {
          economicAttributeCollectionCopy[key + 'N5'] =
            formData.economicAttributeCollection[key as keyof IEconomicAttributeCollection][subKey];
        }
      }
    }

    errorsFormMetaCulture.formError = false;
    setErrorsFormMetaCulture({
      ...validateFormDataMetaCulture(formData, errorsFormMetaCulture, metaCode, metaCultureName),
    });

    setErrorsFormEconomicValue({
      ...validateFormData(economicAttributeCollectionCopy, errorsFormEconomicValue),
    });
    errorsFormDefault.formError = false;
    errorsFormCoProduct.formError = false;
    setErrorCoProductsMessages([]);
    setErrorsFormDefault({ ...validateFormData(formData, errorsFormDefault, null, cultures) });

    let coProductErrorMessagesArray = [];
    for (const key in formData?.coProducts) {
      const validateCoProduct = validateFormData(
        formData.coProducts[key],
        errorsFormCoProduct,
        key,
      );
      if (validateCoProduct.yield.message) {
        coProductErrorMessagesArray.push(validateCoProduct.yield.message);
      }
      setErrorsFormCoProduct({
        ...validateCoProduct,
      });
    }
    setErrorCoProductsMessages(coProductErrorMessagesArray);
    if (
      !errorsFormDefault.formError &&
      !coProductErrorMessagesArray.length &&
      !errorsFormEconomicValue.formError &&
      !errorsFormMetaCulture.formError
    ) {
      setCustomText(
        !selectedAssociateCulture
          ? t('loaders.CREATE_CULTURE_LOADER')
          : t('loaders.UPDATE_CULTURE_LOADER'),
      );
      const formDataCopy = { ...formData };
      formDataCopy.coProducts = formData.coProducts.filter((coProduct) => coProduct.id !== 0);
      formDataCopy.metaCode = formData.metaCode === '' ? null : formData.metaCode;
      formDataCopy.metaCultureName =
        formData.metaCultureName === '' ? null : formData.metaCultureName;
      if (!selectedAssociateCulture) {
        delete formDataCopy.id;
      }

      let cooperativeId = -1;
      if (role && role === 'SUPER_ADMIN')
        cooperativeId = Number(localStorage.getItem('cooperative'));
      {
        !selectedAssociateCulture
          ? addCulture(formDataCopy, cooperativeId)
              .then(() => {
                toast.success(t('success.cultureCreated', { name: formData.name }));
                setFormData(DEFAULT_FORM);
                onSubmitSuccess();
              })
              .catch((err) => {
                if (
                  err.response.data['message'] === 'MetaCode should be a string of 3 characters'
                ) {
                  toast.error(t('errors.cultureCodeInvalid'));
                } else {
                  toast.error(err.response.data['message']);
                }
              })
          : updateCulture(formDataCopy, formData.id)
              .then(() => {
                toast.success(t('success.cultureUpdated', { name: formData.name }));
                onSubmitSuccess();
              })
              .catch((err) => {
                if (
                  err.response.data['message'] === 'MetaCode should be a string of 3 characters'
                ) {
                  toast.error(t('errors.cultureCodeInvalid'));
                } else {
                  toast.error(err.response.data['message']);
                }
              });
      }
    }
  };

  const updateEconomicAttribute = (key: string, value: number, subkey: string) => {
    setFormData((prevData) => {
      const updatedAttribute: IEconomicAttributeCollection = {
        ...prevData.economicAttributeCollection,
      };
      updatedAttribute[key as keyof IEconomicAttributeCollection] = {
        ...updatedAttribute[key as keyof IEconomicAttributeCollection],
        [subkey]: value,
      };
      return {
        ...prevData,
        economicAttributeCollection: updatedAttribute,
      };
    });
  };

  const updateForm = (
    key: string,
    value: number | [number, number, ICoproductType[]] | [number, string] | boolean | string | null,
    subkey?: string,
  ) => {
    let tab = formData.coProducts ? [...formData.coProducts] : [];
    if (key === 'coProducts' && Array.isArray(value) && value.length === 3) {
      const id = value[0];
      const newValue = value[1];
      const displayName = value[2]?.filter(
        (coProduct: ICoproductType) => coProduct.id === newValue,
      )[0].displayName;
      const newCoProduct = {
        id: newValue,
        displayName,
        yield: formData?.coProducts?.[id]?.yield ?? '',
      };
      if (formData.coProducts && formData.coProducts[id]) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          coProducts: prevFormData.coProducts.map((prevCoProduct, index) =>
            id === index ? newCoProduct : prevCoProduct,
          ),
        }));
      } else {
        setFormData((prevFormData) => ({
          ...prevFormData,
          coProducts: prevFormData.coProducts
            ? [...prevFormData.coProducts, newCoProduct]
            : [newCoProduct],
        }));
      }
      updateCoProduct(id, newCoProduct);
    } else if (key === 'yieldProduct' && Array.isArray(value) && value.length == 2) {
      const id = value[0];
      const newValue = value[1];
      if (tab === null) {
        tab = [];
      }
      tab[id] ? (tab[id].yield = newValue) : tab.push({ id: newValue, displayName: '' });
      setFormData({ ...formData });
    } else if (key === 'tfiHerbicide' || key === 'tfiWithoutHerbicide' || key === 'extraCost') {
      setFormData({ ...formData, [key]: emptyFieldToZero(value) });
    } else if (key === 'price' || key === 'inputCostWithoutNitrogen') {
      if (subkey) {
        updateEconomicAttribute(key, Number(value), subkey);
      }
    } else if (key === 'name') {
      setFormData({ ...formData, [key as string]: value });
    } else {
      setFormData({ ...formData, [key]: value });
    }
  };

  useEffect(() => {
    if (inputValue) {
      const selectedMetaCulture = metaculture?.find((item) => item?.metaCode === metaCode);
      if (selectedMetaCulture) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          name: formData?.name ? formData?.name : selectedMetaCulture?.metaCultureName,
          metaCode: selectedMetaCulture?.metaCode,
          metaCultureName: selectedMetaCulture?.metaCultureName,
          aerialBiomass: selectedMetaCulture?.aerialBiomass ?? 0,
          rootBiomass: selectedMetaCulture?.rootBiomass ?? 0,
          capCategory: selectedMetaCulture?.capCategoryName,
          isArable: selectedMetaCulture?.arable,
        }));
      }
    } else {
      resetMetacultureClone();
    }
  }, [metaCode, metaCultureName, inputValue]);

  const handleFieldsCultureChange = (selectedId: string | number) => {
    const parsedId = Number(selectedId);

    if (parsedId === 0) {
      setSelectedAssociateCulture(null);
      setFormData((prevFormData) => ({
        ...prevFormData,
        id: null,
        name: '',
        metaCode: '',
        metaCultureName: '',
        aerialBiomass: 0,
        rootBiomass: 0,
        capCategory: t('common.none'),
        isArable: true,
        cultureSeason: '',
        cultureType: CULTURE_TYPE.main,
      }));
      setInputValue('');
      return;
    }
    const selectedCulture = listAssociateCulture?.find(
      (culture: ICulture) => culture.id === Number(parsedId),
    );

    if (!selectedCulture) {
      return;
    }

    setSelectedAssociateCulture(parsedId);
    setFormData((prevFormData) => ({
      ...prevFormData,
      id: selectedCulture?.id ?? null,
      name: selectedCulture?.name ?? '',
      metaCode: selectedCulture?.metaCode ?? '',
      metaCultureName: selectedCulture?.metaCultureName ?? '',
      aerialBiomass: selectedCulture?.aerialBiomass ?? 0,
      rootBiomass: selectedCulture?.rootBiomass ?? 0,
      capCategory: (selectedCulture?.capCategory as any) ?? t('common.none'),
      isArable: selectedCulture?.isArable ?? false,
      cultureSeason: selectedCulture?.cultureSeason ?? '',
      isOrganic: selectedCulture?.isOrganic ?? false,
      cultureType: selectedCulture?.cultureType ?? CULTURE_TYPE.main,
    }));
    setInputValue(
      selectedCulture?.metaCode && selectedCulture?.metaCultureName
        ? `${selectedCulture?.metaCode} - ${selectedCulture?.metaCultureName}`
        : '',
    );
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = checkTypeStringRegexDatagrid(e.target.value);
    updateForm('name', newName);
  };

  const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const newName = checkTypeStringRegexDatagrid(e.target.value);
    updateForm('name', newName);

    const matchingCulture = listAssociateCulture
      .filter((culture) => culture.isCustomized === false)
      ?.find((culture) => culture.name.toLowerCase() === newName.toLowerCase());

    if (matchingCulture) {
      setSelectedAssociateCulture(matchingCulture.id);
    }
  };

  const handleCheckboxChange = () => {
    const newIsOrganic = !formData.isOrganic;
    updateForm('isOrganic', newIsOrganic);
    setFormData((prevFormData) => ({
      ...prevFormData,
      isOrganic: newIsOrganic,
    }));
    setSelectedAssociateCulture(null);
  };

  useEffect(() => {
    let selectedCultures;
    if (formData.isOrganic) {
      selectedCultures = isOrganic ? cultures : invertedCultures;
    } else {
      selectedCultures = !isOrganic ? cultures : invertedCultures;
    }
    const culturesWithDefaultOption: (ICulture | Partial<ICulture>)[] = [
      defaultCulture,
      ...selectedCultures,
    ];
    setListAssociateCulture(culturesWithDefaultOption as ICulture[]);
  }, [formData.isOrganic]);

  return (
    <div>
      <ThemeProvider theme={customDialogAddCultureTheme}>
        <Dialog
          maxWidth="md"
          fullWidth
          open={modal}
          ref={ref}
          PaperProps={{ ref: ref }}
          onClose={() => setModal(false)}
        >
          <div className="add_culture_container">
            <Box className="modal_header">
              <DialogTitle>{t('title.addCulture')}</DialogTitle>
              <ClearIcon className="close-icon" onClick={() => setModal(false)} />
            </Box>
            <DialogContent>
              <div className="add_culture_select">
                <Box id="cultureCode">
                  <InputLabel id="metaCodeLabel" htmlFor="metaCode">
                    {t('common.cultureCode')}
                  </InputLabel>
                  {renderAutoComplete()}
                </Box>
                <Box id="add_name" className="add_name_container">
                  <InputLabel id="nameLabel" htmlFor="name">
                    {t('common.name')}
                    <span className="label_asterisk">*</span>
                  </InputLabel>
                  <TextField
                    id="name"
                    value={formData.name}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    placeholder={t('placeholder.enterName')}
                    disabled={loading || selectedAssociateCulture !== null}
                    error={
                      (errorsFormDefault?.['name' as keyof IFormErrors] as IFormErrorObjects)
                        .message
                        ? true
                        : false
                    }
                    helperText={
                      (errorsFormDefault?.['name' as keyof IFormErrors] as IFormErrorObjects)
                        .message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel htmlFor="associateCulture">
                    {t('form.correspondingCulture')}
                  </InputLabel>
                  <Select
                    id="associateCulture"
                    sx={{ width: '300px' }}
                    onChange={(e) => handleFieldsCultureChange(e.target.value)}
                    value={selectedAssociateCulture ?? 0}
                  >
                    {listAssociateCulture
                      .filter((cultureList) => cultureList.isCustomized === false)
                      .map((culture) => (
                        <MenuItem key={culture?.id} value={culture?.id}>
                          <ListItemText primary={culture.name} />
                        </MenuItem>
                      ))}
                  </Select>
                </Box>
              </div>
              <fieldset className="add_culture_fieldset">
                <legend>{t('form.metaCultureLegend')}</legend>
                <Box>
                  <InputLabel id="CoefBALabel" htmlFor="coefBA">
                    {t('form.aerialBiomassCoeff')} :
                  </InputLabel>
                  <TextField
                    id="coefBA"
                    placeholder="0"
                    disabled={true}
                    value={formData?.aerialBiomass}
                  />
                </Box>
                <Box>
                  <InputLabel id="CoefBRLabel" htmlFor="coefBR">
                    {t('form.rootBiomassCoeff')} :
                  </InputLabel>
                  <TextField
                    id="coefBR"
                    placeholder="0"
                    disabled={true}
                    value={formData?.rootBiomass}
                  />
                </Box>
                <Box>
                  <InputLabel htmlFor="capCategory">{t('form.capCategoryLabel')} :</InputLabel>
                  <TextField
                    id="capCategory"
                    disabled={true}
                    placeholder={t('common.noneFeminine')}
                    value={formData?.capCategory}
                  />
                </Box>
                <Box>
                  <InputLabel id="isArableLabel" htmlFor="isArable">
                    {t('form.isArable')} :
                  </InputLabel>
                  <Checkbox
                    id="isArable"
                    disabled={true}
                    className="meta_culture_checkbox"
                    checked={formData?.isArable}
                  />
                </Box>
              </fieldset>
              <fieldset className="add_culture_fieldset">
                <legend>{t('form.price')}</legend>
                <Box>
                  <InputLabel id="priceLabel" htmlFor="price">
                    {t('column.sellingPrice')}
                    <span className="label_asterisk">*</span>
                  </InputLabel>
                  <TextField
                    id="price"
                    value={formData.economicAttributeCollection?.price?.currentEconomicValue}
                    onChange={(e) => {
                      updateForm(
                        'price',
                        checkDecimalRegexTwoDigitsDatagridValue(e.target.value),
                        'currentEconomicValue',
                      );
                    }}
                    placeholder={t('placeholder.enterSellingPrice')}
                    disabled={loading}
                    error={
                      (errorsFormEconomicValue?.['price' as keyof IFormErrors] as IFormErrorObjects)
                        .message
                        ? true
                        : false
                    }
                    helperText={
                      (errorsFormEconomicValue?.['price' as keyof IFormErrors] as IFormErrorObjects)
                        .message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel id="inputCostWithoutNitrogenLabel" htmlFor="inputCostWithoutNitrogen">
                    {t('column.inputCost')}
                    <span className="label_asterisk">*</span>
                  </InputLabel>
                  <TextField
                    id="inputCostWithoutNitrogen"
                    value={
                      formData.economicAttributeCollection?.inputCostWithoutNitrogen
                        ?.currentEconomicValue
                    }
                    onChange={(e) => {
                      updateForm(
                        'inputCostWithoutNitrogen',
                        Number(checkDecimalRegexTwoDigitsDatagridValue(e.target.value)),
                        'currentEconomicValue',
                      );
                    }}
                    placeholder={t('placeholder.enterCharge')}
                    disabled={loading}
                    error={
                      (
                        errorsFormEconomicValue?.[
                          'inputCostWithoutNitrogen' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormEconomicValue?.[
                          'inputCostWithoutNitrogen' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel id="extraCostLabel" htmlFor="extraCost">
                    {t('column.extraCost')}
                  </InputLabel>
                  <TextField
                    id="extraCost"
                    value={formData.extraCost}
                    onChange={(e) => {
                      updateForm('extraCost', checkDecimalNegativeDatagrid(e.target.value));
                    }}
                    placeholder={t('placeholder.enterExtraCharge')}
                    disabled={loading}
                    error={
                      (errorsFormDefault?.['extraCost' as keyof IFormErrors] as IFormErrorObjects)
                        .message
                        ? true
                        : false
                    }
                    helperText={
                      (errorsFormDefault?.['extraCost' as keyof IFormErrors] as IFormErrorObjects)
                        .message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel id="priceN5Label" htmlFor="priceN5">
                    {t('form.sellingPriceN5WithUnit')}{' '}
                    <sup style={{ letterSpacing: '0.5px' }}>(1)</sup>
                  </InputLabel>

                  <TextField
                    id="priceN5"
                    value={formData.economicAttributeCollection?.price?.futureEconomicValue}
                    onChange={(e) => {
                      updateForm(
                        'price',
                        checkDecimalRegexTwoDigitsDatagridValue(e.target.value),
                        'futureEconomicValue',
                      );
                    }}
                    placeholder={t('placeholder.enterSellingPriceN5')}
                    disabled={loading}
                    error={
                      (
                        errorsFormEconomicValue?.[
                          'priceN5' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormEconomicValue?.[
                          'priceN5' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel
                    id="inputCostWithoutNitrogenN5Label"
                    htmlFor="inputCostWithoutNitrogenN5"
                  >
                    {t('form.inputCostN5WithUnit')}
                    <sup style={{ letterSpacing: '0.5px' }}>(1)</sup>
                  </InputLabel>
                  <TextField
                    id="inputCostWithoutNitrogenN5"
                    value={
                      formData.economicAttributeCollection?.inputCostWithoutNitrogen
                        ?.futureEconomicValue
                    }
                    onChange={(e) => {
                      updateForm(
                        'inputCostWithoutNitrogen',
                        Number(checkDecimalRegexTwoDigitsDatagridValue(e.target.value)),
                        'futureEconomicValue',
                      );
                    }}
                    placeholder={t('placeholder.enterChargeN5')}
                    disabled={loading}
                    error={
                      (
                        errorsFormEconomicValue?.[
                          'inputCostWithoutNitrogenN5' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormEconomicValue?.[
                          'inputCostWithoutNitrogenN5' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
              </fieldset>
              <fieldset className="add_culture_fieldset">
                <legend>{t('common.yield')}</legend>
                <Box>
                  <InputLabel id="tfiLabelHerbicide" htmlFor="tfiHerbicide">
                    {t('column.tfiHerbicide')}
                  </InputLabel>
                  <TextField
                    id="iftHerbicide"
                    value={formData.tfiHerbicide}
                    onChange={(e) => {
                      updateForm(
                        'tfiHerbicide',
                        checkDecimalRegexTwoDigitsDatagridValue(e.target.value),
                      );
                    }}
                    placeholder={t('placeholder.enterIFTHerbicide')}
                    disabled={loading}
                    error={
                      (
                        errorsFormDefault?.[
                          'tfiHerbicide' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormDefault?.[
                          'tfiHerbicide' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel id="tfiLabelWithoutHerbicide" htmlFor="tfiWithoutHerbicide">
                    {t('column.tfiWithoutHerbicide')}
                  </InputLabel>
                  <TextField
                    id="tfiWithoutHerbicide"
                    value={formData.tfiWithoutHerbicide}
                    onChange={(e) => {
                      updateForm(
                        'tfiWithoutHerbicide',
                        checkDecimalRegexTwoDigitsDatagridValue(e.target.value),
                      );
                    }}
                    placeholder={t('placeholder.enterIFTWithoutHerbicide')}
                    disabled={loading}
                    error={
                      (
                        errorsFormDefault?.[
                          'tfiWithoutHerbicide' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormDefault?.[
                          'tfiWithoutHerbicide' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel id="presenceDurationLabel" htmlFor="presenceDuration">
                    {t('form.presenceDuration')} (an)<span className="label_asterisk">*</span>
                  </InputLabel>
                  <TextField
                    id="presenceDuration"
                    value={formData.presenceDuration}
                    onChange={(e) => {
                      updateForm(
                        'presenceDuration',
                        checkNoDecimalRegexTwoDigitsDatagridValue(e.target.value),
                      );
                    }}
                    placeholder={t('placeholder.enterPresenceDuration')}
                    disabled={loading}
                    error={
                      (
                        errorsFormDefault?.[
                          'presenceDuration' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormDefault?.[
                          'presenceDuration' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel id="prohibitionDurationLabel" htmlFor="prohibitionDuration">
                    {t('common.prohibitionDuration')} (an)<span className="label_asterisk">*</span>
                  </InputLabel>
                  <TextField
                    id="prohibitionDuration"
                    value={formData.prohibitionDuration}
                    onChange={(e) => {
                      updateForm(
                        'prohibitionDuration',
                        checkNoDecimalRegexTwoDigitsDatagridValue(e.target.value),
                      );
                    }}
                    placeholder={t('placeholder.enterProhibitionDuration')}
                    disabled={loading}
                    error={
                      (
                        errorsFormDefault?.[
                          'prohibitionDuration' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormDefault?.[
                          'prohibitionDuration' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel id="yieldLabel" htmlFor="yield">
                    {t('column.yield')}
                    <span className="label_asterisk">*</span>
                  </InputLabel>

                  <TextField
                    id="yield"
                    value={formData.yield}
                    onChange={(e) => {
                      updateForm('yield', checkDecimalRegexTwoDigitsDatagridValue(e.target.value));
                    }}
                    placeholder={t('placeholder.enterYield')}
                    disabled={loading}
                    error={
                      (errorsFormDefault?.['yield' as keyof IFormErrors] as IFormErrorObjects)
                        .message
                        ? true
                        : false
                    }
                    helperText={
                      (errorsFormDefault?.['yield' as keyof IFormErrors] as IFormErrorObjects)
                        .message
                    }
                  />
                </Box>
                <Box>
                  <InputLabel id="irrigationYieldGainLabel" htmlFor="irrigationYieldGain">
                    {t('column.irrigationYieldGain')}
                    <span className="label_asterisk">*</span>
                  </InputLabel>
                  <TextField
                    id="irrigationYieldGain"
                    value={formData.irrigationYieldGain}
                    onChange={(e) => {
                      updateForm(
                        'irrigationYieldGain',
                        checkDecimalRegexTwoDigitsDatagridValue(e.target.value),
                      );
                    }}
                    placeholder={t('placeholder.enterIrrigationYieldGain')}
                    disabled={loading}
                    error={
                      (
                        errorsFormDefault?.[
                          'irrigationYieldGain' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormDefault?.[
                          'irrigationYieldGain' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
              </fieldset>
              <div className="add_culture_select">
                <Box className="irrigationWaterNeededBox">
                  <InputLabel id="irrigationWaterNeededLabel" htmlFor="irrigationWaterNeeded">
                    {t('column.irrigationWaterNeeded')}
                    <span className="label_asterisk">*</span>
                  </InputLabel>

                  <TextField
                    id="irrigationWaterNeeded"
                    value={formData.irrigationWaterNeeded}
                    onChange={(e) => {
                      updateForm(
                        'irrigationWaterNeeded',
                        checkDecimalRegexTwoDigitsDatagridValue(e.target.value),
                      );
                    }}
                    placeholder={t('placeholder.enterIrrigationWater')}
                    disabled={loading}
                    error={
                      (
                        errorsFormDefault?.[
                          'irrigationWaterNeeded' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                        ? true
                        : false
                    }
                    helperText={
                      (
                        errorsFormDefault?.[
                          'irrigationWaterNeeded' as keyof IFormErrors
                        ] as IFormErrorObjects
                      ).message
                    }
                  />
                </Box>
                <div className="cultureSeasonContainer">
                  <div className="wrapper">
                    <Box>
                      <InputLabel htmlFor="cultureSeason">{t('column.season')} :</InputLabel>
                      <Select
                        displayEmpty
                        id="cultureSeason"
                        sx={{ width: '300px' }}
                        onChange={(e) => updateForm('cultureSeason', e.target.value)}
                        value={formData.cultureSeason}
                        disabled={loading || selectedAssociateCulture !== null}
                        error={
                          (
                            errorsFormDefault?.[
                              'cultureSeason' as keyof IFormErrors
                            ] as IFormErrorObjects
                          ).message
                            ? true
                            : false
                        }
                      >
                        {saisonCultures.map((seasonCulture) => (
                          <MenuItem key={seasonCulture?.label} value={seasonCulture?.value}>
                            <ListItemText primary={seasonCulture.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box>
                      <InputLabel htmlFor="cultureType">{t('column.cultureType')}</InputLabel>
                      <Select
                        id="cultureType"
                        sx={{ width: '300px' }}
                        onChange={(e) => updateForm('cultureType', e.target.value)}
                        value={formData.cultureType}
                        disabled={loading || selectedAssociateCulture !== null}
                      >
                        {cultureTypes.map((cultureType) => (
                          <MenuItem key={cultureType?.label} value={cultureType?.value}>
                            <ListItemText primary={cultureType.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </div>
                  {(errorsFormDefault?.['cultureSeason' as keyof IFormErrors] as IFormErrorObjects)
                    .message && (
                    <div className="error_form">
                      {
                        (
                          errorsFormDefault?.[
                            'cultureSeason' as keyof IFormErrors
                          ] as IFormErrorObjects
                        ).message
                      }
                    </div>
                  )}
                </div>
              </div>
              <fieldset className="add_culture_fieldset">
                <legend>
                  {t('form.coProductLegend')}{' '}
                  <ThemeProvider theme={customButtonTheme}>
                    <Button
                      id="add_coProduct_button"
                      onClick={addCoProduct}
                      disabled={checkDisabledCoProductButton}
                    >
                      {t('common.add')}
                    </Button>
                  </ThemeProvider>
                </legend>
                <Box id="add_culture_coProducts_container">
                  {coProductState.length ? null : addCoProduct()}
                  {coProductsTypes.length &&
                    coProductState?.map((coProductStateItem, idx) => {
                      const coProductSelectId = `selectId-${idx}`;
                      const yieldProductId = `yield-${idx}`;
                      const displayName = formData?.coProducts?.[idx]?.displayName;
                      return (
                        <section
                          className="add_culture_coProducts_section"
                          key={`coProduct-${idx}`}
                        >
                          <Box id="coproduct_box">
                            <InputLabel id="coProductTypeLabel" htmlFor={coProductSelectId}>
                              {t('form.coProductType')}
                            </InputLabel>
                            <Select
                              id={coProductSelectId}
                              onChange={(e) =>
                                updateForm('coProducts', [
                                  idx,
                                  Number(e.target.value),
                                  coProductsTypes,
                                ])
                              }
                              value={
                                formData?.coProducts
                                  ? formData?.coProducts[idx]
                                    ? formData?.coProducts[idx].id
                                    : 0
                                  : 0
                              }
                              disabled={checkDisabledCoProduct(idx)}
                              className={errorCoProductsMessages.length ? 'MuiErrorForm' : ''}
                            >
                              {processedCoProductTypes(idx).map((coProductsType) => (
                                <MenuItem key={coProductsType?.id} value={coProductsType?.id}>
                                  <ListItemText primary={coProductsType.displayName} />
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>

                          <Box id="coproduct_box">
                            <InputLabel id="coProductYieldLabel" htmlFor={yieldProductId}>
                              {t('form.coProductYieldLabel', { name: displayName ?? '' })}
                            </InputLabel>
                            <TextField
                              id={yieldProductId}
                              name={yieldProductId}
                              value={
                                formData?.coProducts
                                  ? formData?.coProducts[idx]
                                    ? (formData?.coProducts[idx].yield ?? '')
                                    : ''
                                  : ''
                              }
                              onChange={(e) => {
                                updateForm('yieldProduct', [
                                  idx,
                                  checkDecimalNegativeDatagrid(e.target.value),
                                ]);
                              }}
                              placeholder={t('placeholder.enterCoProductYieldGrain')}
                              disabled={
                                !formData?.coProducts[idx]?.id
                                  ? true
                                  : checkDisabledCoProduct(idx)
                                    ? true
                                    : loading
                              }
                              error={
                                errorCoProductsMessages &&
                                errorCoProductsMessages?.[0]?.includes(
                                  coProductStateItem.displayName,
                                )
                                  ? true
                                  : false
                              }
                              helperText={
                                errorCoProductsMessages &&
                                errorCoProductsMessages?.[0]?.includes(
                                  coProductStateItem.displayName,
                                ) &&
                                errorCoProductsMessages[0]
                              }
                            />
                            <DeleteIcon
                              className="coproduct_delete"
                              onClick={(e) => {
                                removeCoProduct(idx);
                              }}
                            />
                          </Box>
                        </section>
                      );
                    })}
                </Box>
              </fieldset>

              <div
                className="add_culture_checkbox"
                style={{ marginTop: !formData?.color ? '47px' : 0 }}
              >
                <Box className="colorContainer">
                  <ThemeProvider theme={customButtonTheme}>
                    {!formData?.color ? (
                      <Button onClick={handleClick}>{t('form.addColor')}</Button>
                    ) : (
                      <div>
                        <div className="colorBlockContainer">
                          <InputLabel className="color_label">{t('form.colorLabel')}</InputLabel>
                          <div className="colorBlock colorBlock__modal">
                            <div
                              className="colorStyle"
                              style={{
                                background: `${formData.color ?? DEFAULT_FORM.color}`,
                              }}
                              onClick={handleClick}
                            />
                            <Button
                              onClick={() => {
                                updateForm('color', null);
                                handleClose();
                              }}
                              id="colorBlock__button"
                            >
                              {t('common.delete')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    {displayColorPicker ? (
                      <div className="colorPicker">
                        <div className="colorPickerCover" onClick={handleClose} />
                        <SwatchesPicker
                          className="colorPickerPosition"
                          width={500}
                          height={430}
                          color={formData.color ?? DEFAULT_FORM.color}
                          onChangeComplete={(newColor: Color) => {
                            const isUniqueColor = checkUniqueColor(newColor);
                            if (isUniqueColor) {
                              updateForm('color', newColor?.hex);
                              handleClose();
                            }
                          }}
                        />
                      </div>
                    ) : null}
                  </ThemeProvider>
                </Box>
                <Box>
                  <InputLabel htmlFor="isStrawProducer">{t('form.strawProducer')}</InputLabel>
                  <Checkbox
                    id="isStrawProducer"
                    checked={formData.isStrawProducer}
                    onChange={() => {
                      updateForm('isStrawProducer', !formData.isStrawProducer);
                    }}
                  />
                </Box>
                <Box>
                  <InputLabel htmlFor="isIrrigableOnly" id="isIrrigableLabel">
                    {t('form.irrigableOnly')}
                  </InputLabel>
                  <Checkbox
                    id="isIrrigableOnly"
                    checked={formData.isIrrigableOnly}
                    onChange={() => {
                      if (!formData.isIrrigableOnly) {
                        setErrorsFormDefault({
                          ...errorsFormDefault,
                          irrigationWaterNeeded: {
                            ...(errorsFormDefault?.irrigationWaterNeeded as IFormErrorObjects),
                            validator: [
                              ...(errorsFormDefault?.irrigationWaterNeeded as IFormErrorObjects)
                                ?.validator,
                              inputAbsolutePositiveFloatValidator,
                            ],
                          },
                        });
                      } else {
                        setErrorsFormDefault({
                          ...errorsFormDefault,
                          irrigationWaterNeeded: {
                            ...(errorsFormDefault?.irrigationWaterNeeded as IFormErrorObjects),
                            validator: [inputNumberRequired, inputPositiveFloatValidator],
                          },
                        });
                      }
                      updateForm('isIrrigableOnly', !formData.isIrrigableOnly);
                    }}
                  />
                </Box>
                <Box>
                  <InputLabel id="firstSimulateEnabledLabel" htmlFor="firstSimulateEnabledCheckbox">
                    {t('form.activeByDefault')}
                  </InputLabel>
                  <Checkbox
                    id="firstSimulateEnabledCheckbox"
                    checked={formData.firstSimulateEnabled}
                    onChange={() => {
                      updateForm('firstSimulateEnabled', !formData.firstSimulateEnabled);
                    }}
                  />
                </Box>
                <Box>
                  <InputLabel htmlFor="organicCultureLabel">{t('form.organicCulture')}</InputLabel>
                  <Checkbox
                    id="organicCulture"
                    checked={formData.isOrganic}
                    onChange={handleCheckboxChange}
                    className={selectedAssociateCulture !== null ? 'meta_culture_checkbox' : ''}
                    disabled={selectedAssociateCulture !== null}
                  />
                </Box>
              </div>
            </DialogContent>
            <div className="text_helper_culture">
              <div>
                <span className="label_asterisk">*</span> {t('message.required')}
              </div>
              <div>(1) {t('message.n5FormNote')}</div>
            </div>
            <DialogActions>
              <ThemeProvider theme={customButtonTheme}>
                <Button id="add_culture_button" onClick={onSubmit}>
                  {selectedAssociateCulture !== null ? t('common.associate') : t('common.add')}
                </Button>
              </ThemeProvider>
            </DialogActions>
          </div>
        </Dialog>
      </ThemeProvider>
    </div>
  );
};

export default AddCultureModal;
